import { useEffect, useRef, useState } from 'react';
import './Hero.css';
import { colors, fonts } from '../theme';

const PASSPORT_CARDS = [
  {
    id: 'steel',
    tag: 'AUTOMOTIVE',
    lot: 'LOT · SS-2026-0471',
    rows: [
      { lbl: 'Buyer Specification', val: '≥ 450 MPa · frozen' },
      { lbl: 'Mill Test Certificate', val: '462 MPa · verified' },
      { lbl: 'Validation Rule', val: '462 ≥ 450 → Conforms' },
    ],
    hash: '#a41f-9c02-e77b',
    caption: 'signed · independently verifiable',
    holdMs: 5000,
  },
  {
    id: 'battery',
    tag: 'EV & BATTERY',
    lot: 'LOT · CEL-2026-0812',
    rows: [
      { lbl: 'Buyer Specification', val: '210 ± 5 g/m² · frozen' },
      { lbl: 'This Lot', val: '207 g/m² · verified' },
      { lbl: 'Trend', val: '212 → 207 (5 lots) ↓' },
    ],
    hash: '#7c92-3e04-b18f',
    caption: 'flagged for trend review · signed',
    holdMs: 7500,
  },
  {
    id: 'compliance',
    tag: 'EV & BATTERY',
    lot: 'LOT · CEL-2026-0812',
    rows: [
      { lbl: 'EU Battery Reg. 2023/1542', val: 'Addressed · Art. 13' },
      { lbl: 'REACH SVHC (EC 1907/2006)', val: '0 above threshold' },
      { lbl: 'Carbon Footprint Declaration', val: 'Filed · verified' },
    ],
    stamp: 'COMPLIANT',
    hash: '#e51a-6f80-2d9c',
    caption: 'auto-mapped to regulation clauses · signed',
    holdMs: 7500,
  },
];

const PassportCard = () => {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const reducedMotion = useRef(false);

  useEffect(() => {
    reducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  useEffect(() => {
    if (reducedMotion.current || paused) return undefined;
    const timer = setTimeout(() => {
      setActive((i) => (i + 1) % PASSPORT_CARDS.length);
    }, PASSPORT_CARDS[active].holdMs);
    return () => clearTimeout(timer);
  }, [active, paused]);

  const card = PASSPORT_CARDS[active];

  return (
    <div
      className="passport-card"
      aria-hidden="true"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div key={card.id}>
        <div className="pc-head">
          <div>
            <div className="pc-title">Digital Material Passport</div>
            <div className="pc-id">{card.lot} · {card.tag}</div>
          </div>
          <svg width="22" height="22" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ opacity: 0.9 }}>
            <path d="M6 26V6H11L21 20V6H26V26H21L11 12V26H6Z" fill="#00C9A7" />
          </svg>
        </div>
        <div className="pc-rows">
          {card.rows.map((row) => (
            <div className="pc-row" key={row.lbl}><span className="lbl">{row.lbl}</span><span className="val">{row.val}</span></div>
          ))}
        </div>
        <div className="pc-stamp">
          <div className="st-top">{card.stamp || 'CONFORMANT'}</div>
          <div className="st-hash">{card.hash}</div>
        </div>
        <div className="pc-caption">{card.caption}</div>
      </div>

      <div className="pc-dots">
        {PASSPORT_CARDS.map((c, i) => (
          <button
            key={c.id}
            className={`pc-dot${i === active ? ' active' : ''}`}
            aria-label={`Show ${c.tag} example`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>
    </div>
  );
};

const Hero = ({ onOpenContact }) => (
  <section id="hero" style={{ position: 'relative', padding: 'clamp(48px, 10vw, 96px) clamp(20px, 5vw, 48px) clamp(40px, 8vw, 80px)', overflow: 'hidden', background: colors.bg }}>
    <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(0,201,167,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(0,201,167,0.06) 1px, transparent 1px)', backgroundSize: '56px 56px', pointerEvents: 'none' }} />
    <div style={{ position: 'absolute', top: '5%', right: '-5%', width: 560, height: 560, background: 'radial-gradient(circle, rgba(0,201,167,0.08) 0%, transparent 68%)', pointerEvents: 'none' }} />

    <div className="hero-grid" style={{ maxWidth: 1080, margin: '0 auto', position: 'relative' }}>
      <div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10, background: 'rgba(0,201,167,0.15)', border: '2px solid rgba(0,201,167,0.4)', borderRadius: 100, padding: '8px 20px', marginBottom: 28, boxShadow: '0 2px 8px rgba(0,201,167,0.15)' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.teal, boxShadow: '0 0 12px rgba(0,201,167,0.6)' }} />
          <span style={{ color: colors.teal, fontSize: 12, fontFamily: fonts.mono, letterSpacing: '0.12em', fontWeight: 600 }}>DIGITAL MATERIAL PASSPORT</span>
          <span style={{ background: colors.teal, color: '#fff', borderRadius: 4, padding: '2px 6px', fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', fontFamily: fonts.mono }}>BETA</span>
        </div>

        <h1 style={{ fontSize: 'clamp(36px,5.2vw,60px)', fontFamily: fonts.display, lineHeight: 1.18, marginBottom: 22, fontWeight: 500 }}>
          <span style={{ color: colors.navy }}>Manufacturing runs on records.</span><br />
          <span style={{ color: colors.teal, fontWeight: 600 }}>Buyers, regulators, and lenders need proof.</span>
        </h1>

        <p style={{ fontSize: 17, color: colors.slateDark, lineHeight: 1.8, maxWidth: 540, marginBottom: 36 }}>
          Digital Material Passport is the execution layer that turns buyer requirements, regulatory standards, supplier certificates, and production data into deterministic, machine-verifiable proof of conformance — for every incoming material and every outgoing shipment.
        </p>

        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <a href="#how" className="cta" aria-label="See how it works"
            style={{ background: `linear-gradient(135deg,${colors.teal},${colors.tealDark})`, border: 'none', borderRadius: 10, padding: '14px 30px', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 9, transition: 'all 0.2s', boxShadow: '0 4px 18px rgba(0,201,167,0.25)' }}>
            How It Works →
          </a>
          <button onClick={() => onOpenContact('contact')} className="ghost" aria-label="Get in touch"
            style={{ background: 'transparent', border: '1px solid #d0d0d0', borderRadius: 10, padding: '14px 28px', color: colors.slate, fontSize: 15, cursor: 'pointer', transition: 'all 0.2s' }}>
            Get in Touch →
          </button>
        </div>
      </div>

      <PassportCard />
    </div>
  </section>
);

export default Hero;
